import React, { createContext, useRef, useContext, useMemo, useState, useCallback } from 'react';
import { useAsyncDisposable } from '../../hooks/useAsyncDisposable';
import { Basic3dLayoutParams, Bootstrapper } from '../domain/Bootstrapper';

type ContextValue = {
  bootstrapper: Bootstrapper | null;
  // Same instance as `bootstrapper`, but published as soon as it's constructed —
  // before init()'s 3D-scene setup resolves. `configuratorUI`/`rootElement` (and
  // everything the side panel reads) are already populated synchronously in the
  // constructor, so panel-only consumers can mount on this without waiting on the
  // 3D viewer. Consumers that touch `.api` (the 3D API, set inside init()) must keep
  // using `bootstrapper`, not this.
  earlyBootstrapper: Bootstrapper | null;
  canvasRef: (node: HTMLCanvasElement | null) => void;
  canvasElement: HTMLCanvasElement | null;
};

const BootstrapperContext = createContext<ContextValue>({
  bootstrapper: null as any,
  earlyBootstrapper: null,
  canvasRef: () => {},
  canvasElement: null,
});

type Props = {
  children: React.ReactNode;
} & Basic3dLayoutParams;

export const BootstrapperProvider = ({ children, ...rest }: Props) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [canvasReady, setCanvasReady] = useState(false);
  const [canvasElement, setCanvasElement] = useState<HTMLCanvasElement | null>(null);

  // If without3d is true, we don't need to wait for canvas
  const skipCanvas = rest.without3d === true;

  // Callback ref to detect when canvas mounts
  const setCanvasRef = useCallback(
    (node: HTMLCanvasElement | null) => {
      canvasRef.current = node;
      setCanvasElement(node);
      if (node && !canvasReady) {
        setCanvasReady(true);
      }
    },
    [canvasReady],
  );

  const [earlyBootstrapper, setEarlyBootstrapper] = useState<Bootstrapper | null>(null);

  const bootstrapper = useAsyncDisposable(async () => {
    // If we need canvas but it's not ready, wait. This must test `canvasReady` — the
    // same signal the deps below track — and not `canvasRef.current`: callback refs are
    // attached during commit, before this passive effect, so the ref is already set on
    // the very first run and we'd construct a Bootstrapper that the `canvasReady` state
    // flip immediately discards (and that `setEarlyBootstrapper` would have published).
    if (!skipCanvas && !canvasReady) return null;
    const bootstrapper = new Bootstrapper(canvasRef.current!, rest);
    setEarlyBootstrapper(bootstrapper);
    await bootstrapper.init();
    return bootstrapper;
  }, [skipCanvas ? true : canvasReady]);

  const value = useMemo(
    () => ({
      bootstrapper,
      earlyBootstrapper,
      canvasRef: setCanvasRef,
      canvasElement,
    }),
    [bootstrapper, earlyBootstrapper, setCanvasRef, canvasElement],
  );

  // Always render children so canvas can mount
  return <BootstrapperContext.Provider value={value}>{children}</BootstrapperContext.Provider>;
};

export const useBootstrapper = () => {
  const context = useContext(BootstrapperContext);
  return context;
};
